import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { useNavigate } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import { isApiError, GUARDIAN_LINK_ERROR_CODES } from "@bw/shared/api";
import { profileQueryKey } from "../../../common/query/useMyProfile";
import { queryKeys } from "../../../common/query/queryClient";
import {
  useGuardianLinkStatus,
  useRequestGuardianLink,
} from "../../../common/query/useGuardianLink";

const parentEmailSchema = z.object({
  parent_email: z
    .string()
    .trim()
    .min(1, "Enter your parent or guardian's email.")
    .email("Enter a valid email address."),
});

type ParentEmailValues = z.infer<typeof parentEmailSchema>;

/**
 * Parent-approval request state machine, driven only by server link status.
 * `headingLevel` is h1 where the step is the whole page (the consent gate).
 * `onApproved` replaces the default "go home" Continue: the gate re-checks the
 * account in place, so a just-approved player isn't sent round in a loop.
 */
export function GuardianLinkStep({
  headingLevel = "h2",
  onApproved,
}: {
  headingLevel?: "h1" | "h2";
  onApproved?: () => void;
}) {
  const status = useGuardianLinkStatus();
  const request = useRequestGuardianLink();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const {
    register,
    handleSubmit,
    formState: { errors, isValid },
  } = useForm<ParentEmailValues>({
    resolver: zodResolver(parentEmailSchema),
    mode: "onChange",
    defaultValues: { parent_email: "" },
  });

  const errorCode = isApiError(request.error) ? request.error.code : null;
  const isSelfLink = errorCode === GUARDIAN_LINK_ERROR_CODES.SELF_NOT_ALLOWED;
  const isChildNotPending =
    errorCode === GUARDIAN_LINK_ERROR_CODES.CHILD_NOT_PENDING;
  const isDuplicate = errorCode === GUARDIAN_LINK_ERROR_CODES.ALREADY_EXISTS;
  // Multi-guardian (#2691): the child only starts the FIRST guardian link.
  const isBootstrapClosed =
    errorCode === GUARDIAN_LINK_ERROR_CODES.BOOTSTRAP_CLOSED;
  const isRateLimited = errorCode === GUARDIAN_LINK_ERROR_CODES.RATE_LIMITED;

  const onDuplicate = (error: unknown) => {
    if (
      isApiError(error) &&
      error.code === GUARDIAN_LINK_ERROR_CODES.ALREADY_EXISTS
    ) {
      void status.refetch();
    }
  };

  const submit = handleSubmit((values) => {
    request.mutate(values.parent_email.trim(), { onError: onDuplicate });
  });

  const link = status.data;

  if (status.isLoading) {
    return (
      <Stack
        role="status"
        aria-live="polite"
        spacing={1.25}
        sx={{ alignItems: "center", py: 2 }}
      >
        <CircularProgress size={24} />
        <Typography color="text.secondary" variant="body2">
          Checking your account…
        </Typography>
      </Stack>
    );
  }

  // A guardian paused the account (multi-guardian, #2691): the link is still
  // active, so there is nothing to request and nothing to continue to.
  if (link && link.status === "active" && link.child_paused) {
    return (
      <Stack spacing={2} role="status">
        <Typography component={headingLevel} variant="h2">
          A guardian has paused your account
        </Typography>
        <Typography color="text.secondary">
          You can look around. Booking, joining, posting and messages wait until
          a guardian turns your account back on from their Family page.
        </Typography>
      </Stack>
    );
  }

  if (link && link.status === "active") {
    return (
      <Stack spacing={2}>
        <Typography component={headingLevel} variant="h2">
          A guardian approved your account
        </Typography>
        <Typography color="text.secondary">
          You&apos;re all set. Continue to start playing.
        </Typography>
        <Button
          type="button"
          variant="contained"
          size="small"
          fullWidth
          onClick={() => {
            if (onApproved) return onApproved();
            void queryClient.invalidateQueries({ queryKey: profileQueryKey });
            void queryClient.invalidateQueries({ queryKey: queryKeys.guardian.myControls() });
            navigate("/", { replace: true });
          }}
        >
          Continue
        </Button>
      </Stack>
    );
  }

  if (link && link.status === "pending") {
    const resend = () => {
      request.reset();
      request.mutate(link.parent_email, { onError: onDuplicate });
    };

    return (
      <Stack spacing={2}>
        <Typography component={headingLevel} variant="h2">
          We asked your parent or guardian to approve your account
        </Typography>
        <Typography color="text.secondary">
          We sent a request to{" "}
          <Box component="strong" sx={{ color: "text.primary", overflowWrap: "anywhere" }}>
            {link.parent_email}
          </Box>
          . You can book, join and post once they approve it from their account.
        </Typography>
        <Button
          type="button"
          variant="contained"
          size="small"
          fullWidth
          onClick={resend}
          disabled={request.isPending}
          startIcon={
            request.isPending ? (
              <CircularProgress size={16} color="inherit" aria-hidden="true" />
            ) : undefined
          }
        >
          {request.isPending ? "Sending…" : "Resend request"}
        </Button>
        {isDuplicate ? (
          <Alert severity="info" role="status">
            Your request is still active — your parent can approve it anytime
            from their account.
          </Alert>
        ) : request.isError ? (
          <Alert severity="error" role="alert">
            We couldn&apos;t send that again right now. Please try later.
          </Alert>
        ) : null}
      </Stack>
    );
  }

  return (
    <Stack spacing={2}>
      <Typography component={headingLevel} variant="h2">
        Ask a parent or guardian to approve your account
      </Typography>
      <Typography color="text.secondary">
        Because you&apos;re under 13, a parent or guardian needs to approve your
        account before you can book, join, post or send messages. You can look
        around in the meantime. Enter their email and we&apos;ll send them a
        request.
      </Typography>

      <Stack component="form" spacing={2} onSubmit={submit} noValidate>
        <TextField
          label="Parent or guardian email"
          type="email"
          inputMode="email"
          autoComplete="email"
          placeholder="parent@example.com"
          required
          error={Boolean(errors.parent_email)}
          helperText={errors.parent_email?.message}
          {...register("parent_email")}
        />

        {isSelfLink ? (
          <Alert severity="error" role="alert">
            Enter a parent&apos;s email, not your own.
          </Alert>
        ) : isChildNotPending ? (
          <Alert severity="error" role="alert">
            Your account doesn&apos;t need parental approval.
          </Alert>
        ) : isRateLimited ? (
          <Alert severity="error" role="alert">
            You&apos;ve sent several requests today. Wait a day before asking
            again, or ask your parent to check their email.
          </Alert>
        ) : isBootstrapClosed ? (
          <Alert severity="info" role="alert">
            You already have a guardian. They can invite another guardian from
            their Family page.
          </Alert>
        ) : request.isError && !isDuplicate ? (
          <Alert severity="error" role="alert">
            We couldn&apos;t send the request. Please check the email and try
            again.
          </Alert>
        ) : null}

        <Button
          type="submit"
          variant="contained"
          size="small"
          fullWidth
          disabled={!isValid || request.isPending}
          startIcon={
            request.isPending ? (
              <CircularProgress size={16} color="inherit" aria-hidden="true" />
            ) : undefined
          }
        >
          {request.isPending ? "Sending…" : "Send request"}
        </Button>
      </Stack>
    </Stack>
  );
}
